import { useSessionKey } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { CallContextPanel } from "@notifications/CallContextPanel";
import { useFrameRefresh } from "@notifications/useFrameRefresh";
import { useCallContext } from "@phone/callContextClient";
import { CallDetail } from "@phone/CallDetail";
import { CallLog } from "@phone/CallLog";
import { DialPadCard } from "@phone/DialPadCard";
import { HeadsetCard } from "@phone/HeadsetCard";
import { fetchPhoneCall, type PhoneCall } from "@phone/phoneCallsClient";
import { phoneCallsKeys, usePhoneCalls } from "@phone/phoneCallsQueries";
import { useSoftphoneStore } from "@phone/softphone";
import {
  Button,
  Dialog,
  PanelError,
  Skeleton,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useOpenCall } from "@shell/navigation";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

const PANEL = "rounded-md border border-rule bg-card shadow-sheet";

const CALL_FRAMES = ["phone_call"];

interface CallsSearch {
  callId?: string;
}

export const validateCallsSearch = (
  search: Record<string, unknown>,
): CallsSearch => ({
  callId: typeof search.callId === "string" ? search.callId : undefined,
});

export const PhoneRoute = ({ search }: { search: CallsSearch }) => {
  const [dialing, setDialing] = useState(false);
  return (
    <section
      aria-label="Calls"
      className="grid min-h-0 flex-1 grid-cols-[320px_minmax(0,1fr)] gap-4"
    >
      <AsyncBoundary
        skeleton={<CallsSkeleton />}
        fallback={(retry) => (
          <div className={`${PANEL} col-span-2 p-6`}>
            <PanelError label="calls" onRetry={retry} />
          </div>
        )}
      >
        <CallsWorkspace
          callId={search.callId}
          onDial={() => setDialing(true)}
        />
      </AsyncBoundary>
      <Dialog
        open={dialing}
        onClose={() => setDialing(false)}
        title="New call"
        size="sm"
      >
        {dialing && (
          <DialPadCard
            className="border-0 bg-transparent p-0 shadow-none"
            onPlaced={() => setDialing(false)}
          />
        )}
      </Dialog>
    </section>
  );
};

const CallsWorkspace = ({
  callId,
  onDial,
}: {
  callId: string | undefined;
  onDial: () => void;
}) => {
  const sessionKey = useSessionKey();
  const openCall = useOpenCall();
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    usePhoneCalls(sessionKey);
  // No client re-sort: it would only reorder the pages already loaded.
  const calls = useMemo(
    () => data.pages.flatMap((page) => page.calls),
    [data.pages],
  );
  const listedCall = callId ? calls.find((c) => c.id === callId) : null;
  const directCallId = callId && !listedCall ? callId : null;
  const directCallKey = phoneCallsKeys.call(sessionKey, callId ?? "");
  const directCall = useQuery({
    queryKey: directCallKey,
    queryFn: ({ signal }) => fetchPhoneCall(callId!, signal),
    enabled: directCallId !== null,
  });
  useFrameRefresh(CALL_FRAMES, directCallId, directCallKey);
  const selected = callId
    ? (listedCall ?? directCall.data ?? null)
    : (calls[0] ?? null);

  return (
    <>
      <aside
        aria-label="Call log"
        className={`${PANEL} flex min-h-0 flex-col px-[22px] pb-4 pt-5`}
      >
        <div className="flex items-center justify-between gap-3 border-b-[1.5px] border-ink pb-2.5">
          <h1 className="font-display text-xl font-medium text-ink">Calls</h1>
          <Button size="sm" onClick={onDial} data-testid="phone-new-call">
            New call
          </Button>
        </div>
        <div
          className="-mx-2 min-h-0 flex-1 overflow-y-auto px-2 pb-2"
          data-testid="phone-call-log"
        >
          <CallLog
            calls={calls}
            selectedId={selected?.id ?? null}
            onSelect={openCall}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => void fetchNextPage()}
          />
        </div>
        <HeadsetCard className="border-t border-rule pt-3.5" />
      </aside>
      {selected ? (
        <CallWorkspaceDetail key={selected.id} call={selected} />
      ) : directCall.isError ? (
        <div className={`${PANEL} p-6`}>
          <PanelError label="call" onRetry={() => void directCall.refetch()} />
        </div>
      ) : callId ? (
        <div className={`${PANEL} p-6`}>
          <SkeletonStack count={3} className="gap-3" itemClassName="h-16" />
        </div>
      ) : (
        <div className={PANEL} />
      )}
    </>
  );
};

const CallWorkspaceDetail = ({ call }: { call: PhoneCall }) => {
  const locallyActive = useSoftphoneStore(
    (s) => s.answeredCallUuid === call.id && s.call?.state === "active",
  );
  if (call.origin !== "inbound" || call.status === "ended") {
    return <CallDetail call={call} />;
  }
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
      <LiveCallerContext call={call} />
      <CallDetail call={call} showSuggestions={!locallyActive} />
    </div>
  );
};

const LiveCallerContext = ({ call }: { call: PhoneCall }) => {
  const context = useCallContext({ callUuid: call.id, origin: call.origin });
  return (
    <section
      aria-label="Caller context"
      data-dd-privacy="hidden"
      className={`${PANEL} max-h-[min(45dvh,28rem)] shrink-0 overflow-y-auto px-6 py-4`}
    >
      <h2 className="font-display text-lg font-medium text-ink">
        Caller context
      </h2>
      <CallContextPanel context={context} />
    </section>
  );
};

const CallsSkeleton = () => (
  <>
    <div className={`${PANEL} flex flex-col gap-4 px-[22px] pt-5`}>
      <Skeleton className="h-8" />
      <SkeletonStack count={6} className="gap-3" itemClassName="h-14" />
    </div>
    <div className={`${PANEL} flex flex-col gap-4 px-[30px] pt-6`}>
      <Skeleton className="h-12 w-72" />
      <Skeleton className="h-6" />
      <Skeleton className="h-16" />
    </div>
  </>
);
